import {isEqual, uniqWith} from 'lodash';
import React from 'react';

import {useUnitSystem} from 'src/contexts/UnitSystemContext';
import {useOptionalUserProfile} from 'src/hooks/useAuth';
import {useFeatures} from 'src/hooks/useFeatures';
import useForecastTimeSeries, {DynamicalDirectOptions} from 'src/hooks/useForecastTimeSeries';
import useObservationTimeSeries from 'src/hooks/useObservationTimeSeries';
import {Site} from 'src/lib/api/project';
import {
  AllColumns,
  ApiForecastTimeSeries,
  ApiObservationTimeSeries,
  DashboardSection,
  ForecastTimeSeriesQuery,
  ForecastTimesBySource,
  ObservationTimeSeriesQuery,
  Source,
} from 'src/lib/api/series';
import {isDynamicalSource} from 'src/lib/dynamical/datasets';
import {isDynamicalDirectEnabled, isDynamicalShadowEnabled} from 'src/lib/dynamical/flag';
import {useDynamicalShadowComparison} from 'src/lib/dynamical/shadowCompare';

import {VizConfigItem} from './Viz/config';

type ChildProps = {
  forecastQueries: ForecastTimeSeriesQuery[];
  observationQueries: ObservationTimeSeriesQuery[];
  forecastTimeSeriesList: ApiForecastTimeSeries[];
  observationTimeSeriesList: ApiObservationTimeSeries[];
  isLoading: boolean;
};

export type Request = {
  [key in Source]?: {
    columns: AllColumns[];
    aggregationOptions: VizConfigItem['aggregationOptions'];
  };
};

export type Props = {
  site: Site;
  requestedForecastColumnsBySource: Request;
  requestedObservationColumnsBySource: Request;
  forecastTimesBySource: ForecastTimesBySource;
  forecastTimesAreLoading?: boolean;
  dateRange: [Date, Date];
  children: (childProps: ChildProps) => JSX.Element;
  isStaging?: boolean;
  // Which dashboard section this provider's plot belongs to; stamped onto every query
  // so the backend can route the whole plot to H4 or H5 as a unit. Omit for plots
  // outside the sectioned dashboard (gauges, surface).
  section?: DashboardSection;
};

export default function TimeSeriesProvider({
  requestedForecastColumnsBySource,
  requestedObservationColumnsBySource,
  site,
  dateRange,
  forecastTimesBySource,
  forecastTimesAreLoading = false,
  children,
  isStaging = false,
  section,
}: Props) {
  const unitSystem = useUnitSystem();
  const [startDate, endDate] = dateRange;

  const forecastQueries = React.useMemo(
    () =>
      uniqWith(Object.values(site.forecastTimeSeries).flat(), isEqual)
        .filter(
          // Skip sources with no selected init time — an empty `[]` selection would
          // otherwise fetch with `initializationTimes: []`, then re-fetch once selection lands.
          (q) =>
            !!requestedForecastColumnsBySource[q.source] &&
            !!forecastTimesBySource[q.source]?.length
        )
        .map((q) => {
          const {columns, aggregationOptions} = requestedForecastColumnsBySource[q.source]!;
          const query = {
            ...q,
            ...aggregationOptions,
            projectId: site.projectId,
            unitSystem: unitSystem,
            section,
            initializationTimes: forecastTimesBySource[q.source]
              ?.filter((ft) => !!ft)
              .map((forecastTime) => forecastTime.initializationTime),
          };

          if (columns) {
            query['columns'] = columns;
          }

          return query;
        }),
    [requestedForecastColumnsBySource, site, forecastTimesBySource, unitSystem, section]
  );
  // Direct reads need the geometry the backend averages over: the site's own
  // geometry (H5), else the drainage basin, else the forecast point — mirroring
  // the API's get_feature_id resolution.
  const profile = useOptionalUserProfile();
  const hasDynamicalQuery = forecastQueries.some(
    (q) => isDynamicalSource(q.source) && !q.includeFlowContributingAreas
  );
  const wantsDirectReads = isDynamicalDirectEnabled(profile) && hasDynamicalQuery;
  // Shadow comparison fetches both paths and records the diff while serving the
  // API; skip it when direct reads are live, which already exercises them.
  const wantsShadowComparison =
    !wantsDirectReads && hasDynamicalQuery && isDynamicalShadowEnabled(profile);
  const geometryFeatureId =
    site.featureIds?.drainageAreaFeatureId ?? site.featureIds?.forecastPointFeatureId;
  const needsGeometryFeature =
    (wantsDirectReads || wantsShadowComparison) && !site.geometry && geometryFeatureId != null;
  const geometryFeatureRequest = useFeatures(
    needsGeometryFeature ? [geometryFeatureId!] : [],
    {enabled: needsGeometryFeature}
  );
  const siteGeometry = site.geometry ?? geometryFeatureRequest.data?.[0]?.geometry;
  // H4 sites' weather comes from precomputed series, so direct reads emulate the
  // H4 ingestion grids to stay value-identical; H5 sites read the store natively.
  const gridSampling = site.geometry ? 'native' : 'h4';
  const dynamicalDirect: DynamicalDirectOptions | null = React.useMemo(
    () =>
      wantsDirectReads && siteGeometry
        ? {
            geometryBySiteId: {[site.id]: siteGeometry},
            localTimeZone: site.localTimeZone,
            sampling: gridSampling,
          }
        : null,
    [wantsDirectReads, siteGeometry, site.id, site.localTimeZone, gridSampling]
  );

  // Gate the query until the direct-read geometry resolves: fetching in API mode
  // first, then flipping the key when the feature lands, would double-fetch and
  // blank the plot. A failed feature fetch un-gates and runs in API mode.
  const awaitingDirectGeometry =
    wantsDirectReads && needsGeometryFeature && geometryFeatureRequest.isLoading;
  const forecastTimeSeriesRequest = useForecastTimeSeries(forecastQueries, {
    isStaging,
    dynamicalDirect,
    enabled: !awaitingDirectGeometry,
  });
  useDynamicalShadowComparison({
    enabled: wantsShadowComparison,
    queries: forecastQueries,
    geometry: siteGeometry,
    localTimeZone: site.localTimeZone,
    sampling: gridSampling,
    isStaging,
  });

  const observationQueries = React.useMemo(
    () =>
      uniqWith(Object.values(site.observationTimeSeries).flat(), isEqual)
        .filter((q) => !!requestedObservationColumnsBySource[q.source])
        .map((q) => {
          const {columns, aggregationOptions} = requestedObservationColumnsBySource[q.source]!;

          const query = {
            ...q,
            ...aggregationOptions,
            projectId: site.projectId,
            unitSystem: unitSystem,
            section,
            startDate,
            endDate,
          };

          if (columns) {
            query['columns'] = columns;
          }

          return query;
        }),
    [requestedObservationColumnsBySource, site, startDate, endDate, unitSystem, section]
  );

  const observationTimeSeriesRequest = useObservationTimeSeries(observationQueries);

  const isLoading =
    forecastTimesAreLoading ||
    awaitingDirectGeometry ||
    forecastTimeSeriesRequest.isLoading ||
    observationTimeSeriesRequest.isLoading;

  return (
    <>
      {children({
        forecastQueries,
        observationQueries,
        // Default to [] rather than asserting non-null: when nothing is selected (or a
        // query is disabled) `data` is undefined, and consumers expect an array.
        forecastTimeSeriesList: forecastTimeSeriesRequest.data ?? [],
        observationTimeSeriesList: observationTimeSeriesRequest.data ?? [],
        isLoading,
      })}
    </>
  );
}
